import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import OffersService from '~/services/OffersService';
import type {
  CMChecklistManualOverride,
  CMChecklistManualOverrideUpsert,
  CMChecklistStaleManualOverride,
} from '~/services/OffersService';

export const cmChecklistManualOverrideQueryKeys = {
  all: ['offers', 'cm-checklist-manual-overrides'] as const,
  list: (offerId: string | undefined) => [...cmChecklistManualOverrideQueryKeys.all, offerId] as const,
};

export function useGetCMChecklistManualOverrides(
  offerId: string | undefined,
  { enabled = true }: { enabled?: boolean } = {},
) {
  return useQuery({
    queryKey: cmChecklistManualOverrideQueryKeys.list(offerId),
    queryFn: async () => {
      if (!offerId) {
        throw new Error('offerId is required');
      }

      const response = await OffersService.getCMChecklistManualOverrides(offerId);
      return (response.result?.manual_overrides ?? []) as Array<CMChecklistManualOverride>;
    },
    enabled: enabled && !!offerId,
  });
}

export function useUpsertCMChecklistManualOverride(offerId: string | undefined) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload: CMChecklistManualOverrideUpsert) => {
      if (!offerId) {
        throw new Error('offerId is required');
      }

      const response = await OffersService.upsertCMChecklistManualOverride(offerId, payload);
      return response.result as CMChecklistManualOverride;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: cmChecklistManualOverrideQueryKeys.list(offerId) });
    },
  });
}

export function useDeleteCMChecklistManualOverride(offerId: string | undefined) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (itemKey: string) => {
      if (!offerId) {
        throw new Error('offerId is required');
      }

      return OffersService.deleteCMChecklistManualOverride(offerId, itemKey);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: cmChecklistManualOverrideQueryKeys.list(offerId) });
    },
  });
}

export function useReconcileCMChecklistManualOverrides(offerId: string | undefined) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (staleItems: Array<CMChecklistStaleManualOverride>) => {
      if (!offerId) {
        throw new Error('offerId is required');
      }

      return OffersService.reconcileCMChecklistManualOverrides(offerId, staleItems);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: cmChecklistManualOverrideQueryKeys.list(offerId) });
    },
  });
}
